"use strict";
var __createBinding = (this && this.__createBinding) || (Object.create ? (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    var desc = Object.getOwnPropertyDescriptor(m, k);
    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
      desc = { enumerable: true, get: function() { return m[k]; } };
    }
    Object.defineProperty(o, k2, desc);
}) : (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    o[k2] = m[k];
}));
var __setModuleDefault = (this && this.__setModuleDefault) || (Object.create ? (function(o, v) {
    Object.defineProperty(o, "default", { enumerable: true, value: v });
}) : function(o, v) {
    o["default"] = v;
});
var __importStar = (this && this.__importStar) || function (mod) {
    if (mod && mod.__esModule) return mod;
    var result = {};
    if (mod != null) for (var k in mod) if (k !== "default" && Object.prototype.hasOwnProperty.call(mod, k)) __createBinding(result, mod, k);
    __setModuleDefault(result, mod);
    return result;
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.Key = void 0;
const bitcoin = __importStar(require("bitcoinjs-lib"));
const ecpair_1 = require("ecpair");
const secp256k1_1 = __importDefault(require("@bitcoinerlab/secp256k1"));
const ecurve = __importStar(require("ecurve"));
const bech32_1 = require("bech32");
const bytepack_1 = require("@gibme/bytepack");
const big_integer_1 = __importDefault(require("big-integer"));
bitcoin.initEccLib(secp256k1_1.default);
class Key {
    // eslint-disable-next-line no-useless-constructor
    constructor(key_pair) {
        this.key_pair = key_pair;
        this.prefixes = {
            ypub: 0x049d7cb2, // Prefix for P2SH(P2WPKH)
            zpub: 0x04b24746 // Prefix for P2WPKH
        };
    }
    get WIF() {
        return this.key_pair.toWIF();
    }
    get compressed() {
        if (this.key_pair.compressed) {
            return new Key(this.key_pair);
        }
        return new Key((0, ecpair_1.ECPairFactory)(secp256k1_1.default).fromPublicKey(this.key_pair.publicKey, {
            compressed: true
        }));
    }
    get public_key() {
        return this.key_pair.publicKey;
    }
    get private_key() {
        return this.key_pair.privateKey;
    }
    get decompressed() {
        if (!this.key_pair.compressed) {
            return new Key(this.key_pair);
        }
        return new Key((0, ecpair_1.ECPairFactory)(secp256k1_1.default).fromPublicKey(this.key_pair.publicKey, {
            compressed: false
        }));
    }
    /**
     * Legacy
     */
    get p2pkh() {
        var _a;
        (_a = this._p2pkh) !== null && _a !== void 0 ? _a : (this._p2pkh = (() => {
            try {
                const { address } = bitcoin.payments.p2pkh({ pubkey: this.key_pair.publicKey });
                return address;
            }
            catch (_a) { }
        })());
        return this._p2pkh;
    }
    /**
     * Nested Segwit
     */
    get p2sh() {
        var _a;
        (_a = this._p2sh) !== null && _a !== void 0 ? _a : (this._p2sh = (() => {
            try {
                const { address } = bitcoin.payments.p2sh({
                    redeem: bitcoin.payments.p2wpkh({ pubkey: this.compressed.public_key })
                });
                return address;
            }
            catch (_a) { }
        })());
        return this._p2sh;
    }
    /**
     * Native Segwit
     */
    get bech32() {
        var _a;
        (_a = this._bech32) !== null && _a !== void 0 ? _a : (this._bech32 = (() => {
            try {
                const { address } = bitcoin.payments.p2wpkh({ pubkey: this.compressed.public_key });
                return address;
            }
            catch (_a) { }
        })());
        return this._bech32;
    }
    get taproot() {
        var _a;
        (_a = this._taproot) !== null && _a !== void 0 ? _a : (this._taproot = (() => {
            try {
                const public_key = ecurve.Point.decodeFrom(ecurve.getCurveByName('secp256k1'), this.key_pair.publicKey);
                const taproot_public_key = require('bip-schnorr').taproot.taprootConstruct(public_key);
                const words = bech32_1.bech32.toWords(taproot_public_key);
                words.unshift(1);
                return bech32_1.bech32m.encode('bc', words);
            }
            catch (_a) { }
        })());
        return this._taproot;
    }
    static generate(key) {
        if (!key) {
            const key_pair = (0, ecpair_1.ECPairFactory)(secp256k1_1.default).makeRandom({
                compressed: false
            });
            return new Key(key_pair);
        }
        else if (typeof key === 'string') {
            const key_pair = (0, ecpair_1.ECPairFactory)(secp256k1_1.default).fromWIF(key);
            return new Key(key_pair);
        }
        const writer = new bytepack_1.Writer();
        writer.uint256_t((0, big_integer_1.default)(key.toString()), true);
        const key_pair = (0, ecpair_1.ECPairFactory)(secp256k1_1.default).fromPrivateKey(writer.buffer, {
            compressed: false
        });
        return new Key(key_pair);
    }
    addresses(options = {}) {
        var _a, _b, _c, _d, _e, _f;
        (_a = options.legacy) !== null && _a !== void 0 ? _a : (options.legacy = true);
        (_b = options.compatibility) !== null && _b !== void 0 ? _b : (options.compatibility = true);
        (_c = options.compressed) !== null && _c !== void 0 ? _c : (options.compressed = true);
        (_d = options.segwit) !== null && _d !== void 0 ? _d : (options.segwit = true);
        (_e = options.bech32) !== null && _e !== void 0 ? _e : (options.bech32 = true);
        (_f = options.taproot) !== null && _f !== void 0 ? _f : (options.taproot = true);
        const addresses = [];
        const checkAdd = (address) => {
            if (address) {
                addresses.push(address);
            }
        };
        if (options.legacy) {
            checkAdd(this.p2pkh);
        }
        if (options.compressed) {
            checkAdd(this.compressed.p2pkh);
        }
        if (options.compatibility) {
            checkAdd(this.p2sh);
        }
        if (options.segwit) {
            checkAdd(this.bech32);
        }
        if (options.taproot) {
            checkAdd(this.taproot);
        }
        return addresses;
    }
}
exports.Key = Key;
Key.paths = {
    core: 'm/0\'/0\'',
    xpub: 'm/44\'/0\'/0\'',
    ypub: 'm/49\'/0\'/0\'',
    zpub: 'm/84\'/0\'/0\''
};
exports.default = Key;
